
/**
 * Utility functions for image compression and optimization
 */

/**
 * Get compressed image URL by appending compression parameters
 * This function works with CDN services that support URL parameters for compression
 * 
 * @param {string} src - Original image URL
 * @param {object} options - Compression options
 * @returns {string} - URL with compression parameters
 */
export const getCompressedImageUrl = (src, options = {}) => {
  // Default compression options
  const defaults = {
    quality: 75, // Slightly reduced default quality for better performance
    width: null,
    format: 'webp', // WebP for better compression
  };

  const settings = { ...defaults, ...options };
  
  // If it's already a compressed URL or not a valid image URL, return as is
  if (!src || src.includes('?compression=') || !src.match(/\.(jpe?g|png|gif|webp)$/i)) {
    return src;
  }

  // For local images in the public folder, use query parameters
  // that our image optimization middleware will process
  if (src.startsWith('/')) {
    return `${src}?compression=true&quality=${settings.quality}${
      settings.width ? `&width=${settings.width}` : ''
    }&format=${settings.format}`;
  }

  // For external images, return as is (can't compress external images this way)
  return src;
};

/**
 * Create optimized srcset for responsive images
 * 
 * @param {string} src - Original image URL
 * @param {Array} widths - Array of widths to generate srcset for
 * @returns {string} - srcset attribute value
 */
export const generateSrcSet = (src, widths = [640, 750, 828, 1080, 1200]) => {
  if (!src || !src.startsWith('/') || !src.match(/\.(jpe?g|png|gif|webp)$/i)) {
    return '';
  }
  
  return widths
    .map(width => `${getCompressedImageUrl(src, { width, quality: 75 })} ${width}w`)
    .join(', ');
};
